/**
 * A small deterministic random number generator.
 *
 * The weather derives itself from a hash of (seed, day) because it needs one
 * value per day and nothing sequential. A dungeon floor needs *many* values in
 * order — which room, how big, where the corridor turns — so it needs a
 * generator with a position in a stream, not a hash.
 *
 * Deterministic on purpose. A floor is identified by its seed and depth and
 * regenerated on demand, so nothing about its layout has to be saved, and the
 * same descent is the same descent on any device. That is the same reasoning
 * as the weather: seed plus index is the whole state.
 *
 * mulberry32 — small, fast, and good enough for level layout. Not for
 * anything that needs to resist being predicted.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    // Zero is a fixed point for the mixing below, so a zero seed would return
    // the same value forever. Any non-zero constant avoids it.
    this.state = (Math.trunc(seed) || 0x9e3779b9) >>> 0;
  }

  /**
   * Derive an independent stream from a base seed and a label.
   *
   * Lets a floor generate rooms, loot and traps from separate streams, so
   * adding a trap roll cannot shift every room that comes after it.
   */
  static forStream(seed: number, index: number, salt = 0): Rng {
    let mixed = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b);
    mixed = Math.imul(mixed ^ (index + 1), 0xc2b2ae35);
    mixed = Math.imul(mixed ^ (salt + 0x27d4eb2d), 0x165667b1);
    return new Rng(mixed >>> 0);
  }

  /** Next value in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [min, max], inclusive at both ends. */
  int(min: number, max: number): number {
    if (max < min) return min;
    return min + Math.floor(this.next() * (max - min + 1));
  }

  /** True with the given probability. */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  /** A random element, or undefined for an empty list. */
  pick<T>(items: readonly T[]): T | undefined {
    if (items.length === 0) return undefined;
    return items[this.int(0, items.length - 1)];
  }

  /**
   * Fisher-Yates, returning a new array.
   *
   * Copies rather than shuffling in place: callers pass data tables in, and a
   * generator that quietly reorders content is a bug that shows up somewhere
   * else entirely.
   */
  shuffle<T>(items: readonly T[]): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
    return out;
  }
}
